// A carteira dentro do mês: quais clientes cada obrigação alcança, e quantos
// ainda devem.
//
// Puro, sem UM import de runtime (só tipos), com prova própria em
// `app/tests/carteira.mjs`.
//
// ---------------------------------------------------------------------------
// Por que isto é um módulo, e não três `filter` espalhados na tela
// ---------------------------------------------------------------------------
// A pergunta "esta obrigação vale para este cliente?" aparece em quatro lugares
// da tela: na fila de caixas da linha, na contagem de pendências do cartão do
// cliente, no medidor de "quanto falta na carteira" e no que o botão "Marcar os
// N" envia. Escrita quatro vezes, ela divergiria — e a forma de divergir é
// conhecida: alguém esquece que obrigação `geral` vale para TODOS os regimes, e
// o DARF-INSS desaparece da carteira de quem tem só Simples Nacional. É a mesma
// cicatriz de "agregar acesso em JavaScript": uma segunda definição da mesma
// regra, e as duas discordando em silêncio.
//
// ---------------------------------------------------------------------------
// A chave dos feitos é composta, e por um motivo medido
// ---------------------------------------------------------------------------
// A tela lê as marcas de TODOS os clientes de uma vez (uma consulta em vez de
// uma por cliente), então o conjunto precisa distinguir "o cliente A entregou a
// DAS" de "o cliente B entregou a DAS". A chave é `cliente|obrigacao`, montada
// aqui e em nenhum outro lugar: chave montada em dois lugares com separadores
// diferentes é defeito silencioso — a marca simplesmente não aparece.

import type { Regime } from "./calendario";

/** O mínimo que este módulo precisa saber de uma obrigação. */
export type AlcanceDaObrigacao = {
  id: string;
  /** Vale para todo regime. Quando verdadeiro, `regimes` é irrelevante. */
  geral: boolean;
  regimes: Regime[];
};

/** O mínimo que este módulo precisa saber de um cliente. */
export type ClienteDaCarteira = {
  id: string;
  nome: string;
  regime: Regime;
};

export function chaveDoFeito(clienteId: string, obrigacaoId: string): string {
  return `${clienteId}|${obrigacaoId}`;
}

/**
 * Esta obrigação alcança este cliente?
 *
 * Obrigação `geral` alcança TODOS — e é por isso que este `if` vem primeiro e
 * mora aqui: escondê-la por causa de um regime faria o contador perder o
 * DARF-INSS por ter marcado "Simples Nacional".
 */
export function alcancaORegime(o: AlcanceDaObrigacao, regime: Regime): boolean {
  if (o.geral) return true;
  return o.regimes.includes(regime);
}

/** Os clientes que esta obrigação alcança, na ordem em que a lista veio. */
export function clientesAlcancados(
  o: AlcanceDaObrigacao,
  clientes: ClienteDaCarteira[],
): ClienteDaCarteira[] {
  return clientes.filter((c) => alcancaORegime(o, c.regime));
}

/** Os que ela alcança e que ainda NÃO têm marca de feito neste mês. */
export function clientesQueDevem(
  o: AlcanceDaObrigacao,
  clientes: ClienteDaCarteira[],
  feitos: Set<string>,
): ClienteDaCarteira[] {
  return clientesAlcancados(o, clientes).filter((c) => !feitos.has(chaveDoFeito(c.id, o.id)));
}

export type PendenciaDoCliente = {
  cliente: ClienteDaCarteira;
  /** Quantas obrigações do mês alcançam este cliente. */
  alcancam: number;
  /** Quantas dessas ainda não foram marcadas. */
  devem: number;
};

/**
 * A carteira ordenada por quem tem mais pendência.
 *
 * Ordena por `devem` desc e desempata por NOME — nunca por id. Desempate por
 * uuid é sorteio, e a mesma tela daria duas ordens em dois carregamentos: é a
 * cicatriz do desempate da `vw_contrato_a_assinar`, que fez um teste passar numa
 * rodada e falhar na outra.
 */
export function pendenciasDaCarteira(
  obrigacoes: AlcanceDaObrigacao[],
  clientes: ClienteDaCarteira[],
  feitos: Set<string>,
): PendenciaDoCliente[] {
  return clientes
    .map((cliente) => {
      const alcancam = obrigacoes.filter((o) => alcancaORegime(o, cliente.regime));
      return {
        cliente,
        alcancam: alcancam.length,
        devem: alcancam.filter((o) => !feitos.has(chaveDoFeito(cliente.id, o.id))).length,
      };
    })
    .sort((a, b) =>
      a.devem === b.devem
        ? a.cliente.nome.localeCompare(b.cliente.nome, "pt-BR")
        : b.devem - a.devem,
    );
}

export type ResumoDaCarteira = {
  clientes: number;
  /** Pares (cliente, obrigação) que o mês pede. */
  pares: number;
  /** Quantos desses já têm marca. */
  entregues: number;
  /** Clientes com ao menos uma pendência. */
  comPendencia: number;
};

/**
 * Os três fatos da carteira no mês.
 *
 * Conta PARES, não obrigações: o trabalho de um contador com 12 clientes num mês
 * de 14 prazos não é 14, é até 168. E `entregues` sai da MESMA travessia que
 * `pares`, não de `feitos.size` — o conjunto pode ter marca de obrigação que o
 * filtro escondeu ou que não alcança mais aquele cliente (o regime dele mudou),
 * e aí `size` afirmaria entrega que o mês não pediu.
 */
export function resumoDaCarteira(
  obrigacoes: AlcanceDaObrigacao[],
  clientes: ClienteDaCarteira[],
  feitos: Set<string>,
): ResumoDaCarteira {
  let pares = 0;
  let entregues = 0;
  let comPendencia = 0;
  for (const c of clientes) {
    let devendo = 0;
    for (const o of obrigacoes) {
      if (!alcancaORegime(o, c.regime)) continue;
      pares += 1;
      if (feitos.has(chaveDoFeito(c.id, o.id))) entregues += 1;
      else devendo += 1;
    }
    if (devendo > 0) comPendencia += 1;
  }
  return { clientes: clientes.length, pares, entregues, comPendencia };
}
